import { useRef, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconLifebuoy, IconSend } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useAssistantStore, usePageContext } from "./useAssistant";
import AssistantMessage from "./AssistantMessage";
import AssistantWelcome from "./AssistantWelcome";
import AssistantHistoryMenu from "./AssistantHistoryMenu";
import AssistantEscalateButton from "./AssistantEscalateButton";

export default function AssistantPanel() {
  const { t } = useTranslation();
  const messages = useAssistantStore((s) => s.messages);
  const isLoading = useAssistantStore((s) => s.isLoading);
  const isFlagged = useAssistantStore((s) => s.isFlagged);
  const sendMessage = useAssistantStore((s) => s.sendMessage);
  const pageContext = usePageContext();
  const [userMessage, setUserMessage] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = () => {
    const text = userMessage.trim();
    if (!text || isLoading) return;
    setUserMessage("");
    sendMessage(text, pageContext);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack h="100%" gap={0}>
      <Group justify="space-between" p="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <Text size="sm" fw={600}>{t("assistant.title")}</Text>
        <Group gap={4}>
          <AssistantEscalateButton />
          <AssistantHistoryMenu />
        </Group>
      </Group>

      <ScrollArea
        style={{ flex: 1 }}
        p="xs"
        viewportRef={scrollRef}
      >
        <Stack gap="md">
          {messages.length === 0 ? (
            <AssistantWelcome pageContext={pageContext} />
          ) : (
            messages.map((msg, i) => (
              <AssistantMessage key={i} message={msg} />
            ))
          )}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <Group gap="xs">
              <Loader size={14} />
              <Text size="xs" c="dimmed">{t("assistant.thinking")}</Text>
            </Group>
          )}
        </Stack>
      </ScrollArea>

      {isFlagged && (
        <Alert
          color="yellow"
          icon={<IconLifebuoy size={16} />}
          radius={0}
          py="xs"
          styles={{ message: { fontSize: "var(--mantine-font-size-xs)" } }}
        >
          {t("assistant.escalate.flaggedBanner")}
        </Alert>
      )}

      <Group
        gap="xs"
        p="xs"
        align="flex-end"
        style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
      >
        <Textarea
          autosize
          minRows={1}
          maxRows={4}
          value={userMessage}
          onChange={(e) => setUserMessage(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("assistant.placeholder")}
          disabled={isLoading}
          style={{ flex: 1 }}
          size="sm"
        />
        <ActionIcon
          variant="filled"
          size="lg"
          onClick={handleSend}
          loading={isLoading}
          disabled={!userMessage.trim()}
        >
          <IconSend size={16} />
        </ActionIcon>
      </Group>
    </Stack>
  );
}
